import React from "react";

const BulkProgressModal = ({ progress, onClose }) => {
  if (!progress) return null;

  const { jobId, status, progress: jobProgress, startTime, estimatedTime, duration } = progress;
  const progressPercentage = jobProgress.total > 0 ? Math.round((jobProgress.processed / jobProgress.total) * 100) : 0;
  
  // Calculate estimated time remaining
  const elapsedTime = duration || (Date.now() - startTime.getTime());
  const avgTimePerUser = elapsedTime / Math.max(jobProgress.processed, 1);
  const remainingUsers = jobProgress.total - jobProgress.processed;
  const estimatedRemaining = Math.max(0, Math.round((remainingUsers * avgTimePerUser) / 1000));

  // Format time display
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDuration = (ms) => {
    return formatTime(Math.round(ms / 1000));
  };

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              🔄 Bulk Processing Progress
            </h5>
            {status === 'completed' && (
              <button type="button" className="btn-close" onClick={onClose}></button>
            )}
          </div>
          
          <div className="modal-body">
            {/* Job Info */}
            <div className="row mb-20">
              <div className="col-12">
                <div className="bg-light rounded-16 px-20 py-15">
                  <h6 className="text-14 fw-500 mb-10">Job Information</h6>
                  <div className="row">
                    <div className="col-sm-6">
                      <small className="text-dark">Job ID:</small>
                      <div className="text-12 fw-500 text-dark" style={{ fontFamily: 'monospace' }}>{jobId}</div>
                    </div>
                    <div className="col-sm-6">
                      <small className="text-dark">Status:</small>
                      <div className="text-12 fw-500">
                        <span className={`badge ${
                          status === 'processing' ? 'bg-primary' : 
                          status === 'completed' ? 'bg-success' : 'bg-danger'
                        }`}>
                          {status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="row mb-20">
              <div className="col-12">
                <div className="d-flex justify-between items-center mb-10">
                  <span className="text-14 fw-500">Overall Progress</span>
                  <span className="text-14 fw-500">{progressPercentage}%</span>
                </div>
                <div className="progress" style={{ height: '8px' }}>
                  <div 
                    className="progress-bar bg-primary" 
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className="text-12 text-dark mt-5">
                  {jobProgress.processed} of {jobProgress.total} users processed
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="row mb-20">
              <div className="col-12">
                <h6 className="text-14 fw-500 mb-15">Processing Statistics</h6>
                <div className="row">
                  <div className="col-sm-3 col-6">
                    <div className="text-center p-10 bg-light rounded-8">
                      <div className="text-20 fw-700 text-green-1">{jobProgress.successful}</div>
                      <div className="text-11 text-dark">Created</div>
                    </div>
                  </div>
                  <div className="col-sm-3 col-6">
                    <div className="text-center p-10 bg-light rounded-8">
                      <div className="text-20 fw-700 text-orange-1">{jobProgress.duplicates}</div>
                      <div className="text-11 text-dark">Duplicates</div>
                    </div>
                  </div>
                  <div className="col-sm-3 col-6">
                    <div className="text-center p-10 bg-light rounded-8">
                      <div className="text-20 fw-700 text-red-1">{jobProgress.failed}</div>
                      <div className="text-11 text-dark">Failed</div>
                    </div>
                  </div>
                  <div className="col-sm-3 col-6">
                    <div className="text-center p-10 bg-light rounded-8">
                      <div className="text-20 fw-700 text-dark">{jobProgress.processed}</div>
                      <div className="text-11 text-dark">Processed</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Progress */}
            {(jobProgress.emailsSent > 0 || jobProgress.emailsFailed > 0) && (
              <div className="row mb-20">
                <div className="col-12">
                  <h6 className="text-14 fw-500 mb-15">📧 Email Status</h6>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="text-center p-10 bg-light rounded-8">
                        <div className="text-18 fw-700 text-green-1">{jobProgress.emailsSent}</div>
                        <div className="text-11 text-dark">Emails Sent</div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="text-center p-10 bg-light rounded-8">
                        <div className="text-18 fw-700 text-red-1">{jobProgress.emailsFailed}</div>
                        <div className="text-11 text-dark">Email Failures</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Timing Information */}
            <div className="row mb-20">
              <div className="col-12">
                <h6 className="text-14 fw-500 mb-15">⏱️ Timing</h6>
                <div className="row">
                  <div className="col-sm-4">
                    <small className="text-dark">Elapsed Time:</small>
                    <div className="text-14 fw-500 text-dark">{formatDuration(elapsedTime)}</div>
                  </div>
                  {status === 'processing' && (
                    <div className="col-sm-4">
                      <small className="text-dark">Est. Remaining:</small>
                      <div className="text-14 fw-500 text-dark">{formatTime(estimatedRemaining)}</div>
                    </div>
                  )}
                  {estimatedTime && (
                    <div className="col-sm-4">
                      <small className="text-dark">Est. Total:</small>
                      <div className="text-14 fw-500 text-dark">{formatTime(estimatedTime.estimatedSeconds)}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {status === 'processing' && (
              <div className="alert alert-info">
                <div className="d-flex items-center">
                  <div className="spinner-border spinner-border-sm mr-10"></div>
                  <div>
                    <strong>Processing in progress...</strong><br/>
                    <small>Please keep this window open. You can check email progress and results once completed.</small>
                  </div>
                </div>
              </div>
            )}

            {status === 'completed' && (
              <div className="alert alert-success">
                <strong>✅ Processing completed successfully!</strong><br/>
                All users have been processed. Check the detailed results below.
              </div>
            )}

            {status === 'failed' && (
              <div className="alert alert-danger">
                <strong>❌ Processing failed</strong><br/>
                {progress.error && <small>{progress.error}</small>}
              </div>
            )}
          </div>

          <div className="modal-footer">
            {status === 'processing' ? (
              <div className="text-12 text-dark">
                <i className="icon-info mr-5"></i>
                Processing will continue in the background if you close this modal
              </div>
            ) : (
              <button type="button" className="button -md bg-purple-1 text-white" onClick={onClose}>
                {status === 'completed' ? 'View Results' : 'Close'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkProgressModal; 